import React, { useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Popconfirm,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useCreditPackages } from '../hooks';
import { CreditPackage } from '../../api/services/adminService';

const { Title, Text } = Typography;
const ORANGE = '#fe5a1d';

const CreditPackages: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const {
    packages,
    loading,
    modalVisible,
    editingPackage,
    submitting,
    setModalVisible,
    handleCreate,
    handleUpdate,
    handleDelete,
    openCreateModal,
    openEditModal,
    refetch,
  } = useCreditPackages();

  useEffect(() => {
    if (modalVisible && editingPackage) {
      form.setFieldsValue({
        label: editingPackage.label,
        credits: editingPackage.credits,
        price: editingPackage.price,
        sortOrder: editingPackage.sortOrder,
        isActive: editingPackage.isActive,
      });
    } else if (modalVisible && !editingPackage) {
      form.resetFields();
      form.setFieldsValue({ isActive: true, sortOrder: 0 });
    }
  }, [modalVisible, editingPackage, form]);

  const onFinish = (values: any) => {
    if (editingPackage) {
      handleUpdate(editingPackage.id, values);
    } else {
      handleCreate(values);
    }
  };

  const columns = [
    {
      title: t('admin.credit_packages.col_label'),
      dataIndex: 'label',
      key: 'label',
      render: (v: string) => (
        <Space>
          <CreditCardOutlined style={{ color: ORANGE }} />
          <Text strong>{v}</Text>
        </Space>
      ),
    },
    {
      title: t('admin.credit_packages.col_credits'),
      dataIndex: 'credits',
      key: 'credits',
      render: (v: number) => (
        <Tag color="blue" style={{ fontWeight: 600 }}>
          {t('admin.credit_packages.col_credits_unit', { count: v })}
        </Tag>
      ),
    },
    {
      title: t('admin.credit_packages.col_price'),
      dataIndex: 'price',
      key: 'price',
      render: (v: number) => (
        <Text strong style={{ color: ORANGE }}>
          {Number(v).toFixed(2)} ALL
        </Text>
      ),
    },
    {
      title: t('admin.credit_packages.col_sort_order'),
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      render: (v: number) => <Text type="secondary">{v}</Text>,
    },
    {
      title: t('admin.credit_packages.col_status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (v: boolean) => (
        <Tag color={v ? 'green' : 'default'}>{v ? t('admin.credit_packages.status_active') : t('admin.credit_packages.status_inactive')}</Tag>
      ),
    },
    {
      title: t('admin.credit_packages.col_created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => (
        <Text type="secondary">{new Date(v).toLocaleDateString()}</Text>
      ),
    },
    {
      title: t('admin.credit_packages.col_actions'),
      key: 'actions',
      render: (_: any, record: CreditPackage) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
            style={{ color: ORANGE }}
          />
          <Popconfirm
            title={t('admin.credit_packages.delete_confirm')}
            description={t('admin.credit_packages.delete_confirm_desc')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('admin.credit_packages.btn_delete')}
            okType="danger"
            cancelText={t('admin.credit_packages.btn_cancel')}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>{t('admin.credit_packages.title')}</Title>
          <Text type="secondary">{t('admin.credit_packages.subtitle')}</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={refetch} loading={loading} className="admin-filter-btn">
            {t('admin.credit_packages.btn_refresh')}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            className="ant-btn-primary admin-filter-btn admin-filter-btn-primary"
          >
            {t('admin.credit_packages.btn_add')}
          </Button>
        </Space>
      </div>

      {/* Table */}
      <Card bordered={false} className="admin-table-card">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={packages}
          columns={columns}
          pagination={false}
          locale={{ emptyText: t('admin.credit_packages.empty_text') }}
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={
          <Space>
            <CreditCardOutlined style={{ color: 'var(--admin-primary)' }} />
            {editingPackage ? t('admin.credit_packages.modal_edit_title') : t('admin.credit_packages.modal_add_title')}
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        onOk={() => form.submit()}
        okText={editingPackage ? t('admin.credit_packages.modal_save') : t('admin.credit_packages.modal_create')}
        okButtonProps={{ className: 'ant-btn-primary' }}
        cancelText={t('admin.credit_packages.btn_cancel')}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ marginTop: 16 }}>
          <Form.Item
            name="label"
            label={t('admin.credit_packages.label_label')}
            rules={[{ required: true, message: t('admin.credit_packages.validation_label') }]}
          >
            <Input placeholder={t('admin.credit_packages.placeholder_name')} />
          </Form.Item>
          <Form.Item
            name="credits"
            label={t('admin.credit_packages.label_credits')}
            rules={[{ required: true, message: t('admin.credit_packages.validation_credits') }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder={t('admin.credit_packages.placeholder_credits')} />
          </Form.Item>
          <Form.Item
            name="price"
            label={t('admin.credit_packages.label_price')}
            rules={[{ required: true, message: t('admin.credit_packages.validation_price') }]}
          >
            <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder={t('admin.credit_packages.placeholder_price')} />
          </Form.Item>
          <Form.Item name="sortOrder" label={t('admin.credit_packages.label_sort_order')}>
            <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
          </Form.Item>
          <Form.Item name="isActive" label={t('admin.credit_packages.label_active')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default CreditPackages;
